import type { ReactNode } from 'react';
import type { Basis, CalcMethod } from '@everplan/shared';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

export interface ChargeFormulaTooltipContext {
  lineItemTotal: number;
  addOnTotal: number;
  sectionTotals: Record<string, number>;
  siblingResolvedAmounts: Record<string, number>;
}

export interface ChargeFormulaTooltipProps {
  calcMethod: CalcMethod;
  rateBps: number | null;
  basis: Basis | null;
  packageContext: ChargeFormulaTooltipContext | null;
  sectionLabels?: Record<string, string>;
  chargeLabels?: Record<string, string>;
  children: ReactNode;
}

function computeBasisTotal(basis: Basis, ctx: ChargeFormulaTooltipContext): number {
  switch (basis.kind) {
    case 'package_subtotal':
      return ctx.lineItemTotal + ctx.addOnTotal;
    case 'sections':
      return basis.sections.reduce((sum, s) => sum + (ctx.sectionTotals[s] ?? 0), 0);
    case 'charges':
      return basis.chargeIds.reduce((sum, id) => sum + (ctx.siblingResolvedAmounts[id] ?? 0), 0);
    case 'composite':
      return basis.parts.reduce((sum, p) => sum + computeBasisTotal(p, ctx), 0);
  }
}

function formatBasisLabel(
  basis: Basis,
  sectionLabels?: Record<string, string>,
  chargeLabels?: Record<string, string>,
): string {
  switch (basis.kind) {
    case 'package_subtotal':
      return 'subtotal';
    case 'sections':
      return basis.sections
        .map((s) => sectionLabels?.[s] ?? (s.charAt(0).toUpperCase() + s.slice(1)))
        .join(' + ');
    case 'charges':
      return basis.chargeIds.map((id) => chargeLabels?.[id] ?? 'a charge').join(' + ');
    case 'composite':
      return basis.parts.map((p) => formatBasisLabel(p, sectionLabels, chargeLabels)).join(' + ');
  }
}

function formatRate(rateBps: number): string {
  return `${(rateBps / 100).toFixed(2).replace(/\.00$/, '')}%`;
}

function formatDollars(cents: number): string {
  return (cents / 100).toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
  });
}

function bankersRound(value: number): number {
  const sign = value < 0 ? -1 : 1;
  const abs = Math.abs(value);
  const truncated = Math.trunc(abs);
  const frac = abs - truncated;
  let rounded: number;
  if (Math.abs(frac - 0.5) < 1e-9) {
    rounded = truncated % 2 === 0 ? truncated : truncated + 1;
  } else {
    rounded = Math.round(abs);
  }
  return sign * (rounded === 0 ? 0 : rounded);
}

/**
 * Wraps `children` in a hover tooltip that explains a percentage charge's
 * formula: "9% of Food & Beverage" plus "9% × $9,405.00 = $846.45". Renders
 * `children` unchanged when:
 *   - calcMethod is 'flat' (the dollar amount is already explicit), or
 *   - rate / basis / packageContext aren't all available (no breakdown to show).
 */
export function ChargeFormulaTooltip({
  calcMethod,
  rateBps,
  basis,
  packageContext,
  sectionLabels,
  chargeLabels,
  children,
}: ChargeFormulaTooltipProps) {
  if (
    calcMethod !== 'percentage' ||
    rateBps == null ||
    basis == null ||
    packageContext == null
  ) {
    return <>{children}</>;
  }

  const basisTotal = computeBasisTotal(basis, packageContext);
  const computed = bankersRound((rateBps * basisTotal) / 10000);
  const basisLabel = formatBasisLabel(basis, sectionLabels, chargeLabels);

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side="top" align="end">
          <div className="space-y-0.5 text-left">
            <div className="font-medium">
              {formatRate(rateBps)} of {basisLabel}
            </div>
            <div className="tabular-nums opacity-80">
              {formatRate(rateBps)} × {formatDollars(basisTotal)} = {formatDollars(computed)}
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
